import React, { useEffect, useState } from "react";
import { Search, X, Clock, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtNL } from "@/lib/dates";

const STATUS = { gepland: "Gepland", bevestigd: "Bevestigd", gestart: "Bezig", afgerond: "Afgerond" };
const DAY = ["zo", "ma", "di", "wo", "do", "vr", "za"];

export function PlanbordSearch({ open, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/assignments/search", { params: { q: term } })
        .then((r) => setResults(r.data))
        .catch((err) => console.warn("Zoeken mislukt", err))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[85] bg-slate-900/50 flex items-start justify-center p-4 pt-[10vh]" onClick={onClose} data-testid="planbord-search-modal">
      <div className="pb-card bg-white w-full max-w-2xl overflow-hidden flex flex-col max-h-[75vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b-2 border-slate-900">
          <Search size={18} className="text-slate-500 shrink-0"/>
          <input autoFocus className="flex-1 outline-none text-base font-semibold bg-transparent" placeholder="Zoek op project, adres, plaats, klant of notitie…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="planbord-search-input"/>
          <button type="button" onClick={onClose} aria-label="Sluiten" data-testid="planbord-search-close"><X size={18}/></button>
        </div>
        <div className="overflow-y-auto pb-scrollbar">
          {q.trim().length < 2 ? (
            <div className="p-6 text-sm text-slate-500 text-center">Typ minimaal 2 letters. Tip: druk <b>Ctrl+K</b> of <b>/</b> om dit venster te openen.</div>
          ) : loading && results.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">Zoeken…</div>
          ) : results.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center" data-testid="planbord-search-empty">Niets gevonden</div>
          ) : results.map((r) => {
            const d = new Date(`${r.date}T12:00:00`);
            return (
              <button key={r.id} type="button" onClick={() => onPick(r)} className="w-full text-left px-3 py-2 border-b border-slate-200 hover:bg-orange-50 flex items-center gap-3" data-testid={`planbord-search-result-${r.id}`}>
                <div className="w-16 shrink-0 text-center">
                  <div className="text-[10px] uppercase font-black text-slate-500">{DAY[d.getDay()]}</div>
                  <div className="text-sm font-black">{fmtNL(d)}</div>
                </div>
                <span className="w-3 h-10 rounded-sm border border-slate-900 shrink-0" style={{ background: r.project_color }}/>
                <div className="min-w-0 flex-1">
                  <div className="font-black text-sm truncate">{r.project_name}</div>
                  <div className="text-xs text-slate-600 truncate">{[r.address, r.city].filter(Boolean).join(", ")}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1 whitespace-nowrap"><Clock size={11}/>{r.start_time}–{r.end_time}</span>
                    <span className="flex items-center gap-1 truncate"><Users2 size={11}/>{r.members.join(", ") || "—"}</span>
                  </div>
                </div>
                <span className="text-[10px] uppercase font-black px-1.5 py-0.5 rounded bg-slate-100 shrink-0">{r.published ? STATUS[r.status] || r.status : "Concept"}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
